import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Settings, Globe, Shield, Bell, Database, Mail,
  Save, RefreshCw, Lock, Eye, EyeOff
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';

const SystemSettings = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [settings, setSettings] = useState({
    siteName: 'وازن للمحاماة',
    siteUrl: 'https://wazin.law',
    adminEmail: 'admin@wazin.law',
    supportEmail: 'support@wazin.law',
    enableRegistration: true,
    enableOTP: true,
    requireEmailVerification: false,
    sessionTimeout: '60',
    maxLoginAttempts: '5',
    enableNotifications: true,
    enableEmailNotif: true,
    enableSMSNotif: false,
    backupEnabled: true,
    backupFrequency: 'daily'
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error(i18n.language === 'ar' ? 'غير مصرح' : 'Unauthorized');
      navigate('/dashboard');
      return;
    }
  }, []);

  const handleSave = () => {
    toast.success(i18n.language === 'ar' ? 'تم حفظ الإعدادات بنجاح' : 'Settings saved successfully');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                {i18n.language === 'ar' ? 'إعدادات النظام' : 'System Settings'}
              </h1>
              <p className="text-text-secondary text-lg">
                {i18n.language === 'ar' ? 'إدارة إعدادات وتكوينات المنصة' : 'Manage platform settings and configurations'}
              </p>
            </div>
            <Button className="btn-primary" onClick={handleSave}>
              <Save className="w-5 h-5 ml-2" />
              {i18n.language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
            </Button>
          </div>

          <Tabs defaultValue="general" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="general">
                <Globe className="w-4 h-4 ml-2" />
                {i18n.language === 'ar' ? 'عام' : 'General'}
              </TabsTrigger>
              <TabsTrigger value="security">
                <Shield className="w-4 h-4 ml-2" />
                {i18n.language === 'ar' ? 'الأمان' : 'Security'}
              </TabsTrigger>
              <TabsTrigger value="notifications">
                <Bell className="w-4 h-4 ml-2" />
                {i18n.language === 'ar' ? 'الإشعارات' : 'Notifications'}
              </TabsTrigger>
              <TabsTrigger value="backup">
                <Database className="w-4 h-4 ml-2" />
                {i18n.language === 'ar' ? 'النسخ الاحتياطي' : 'Backup'}
              </TabsTrigger>
              <TabsTrigger value="email">
                <Mail className="w-4 h-4 ml-2" />
                {i18n.language === 'ar' ? 'البريد' : 'Email'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'الإعدادات العامة' : 'General Settings'}</CardTitle>
                  <CardDescription>
                    {i18n.language === 'ar' ? 'إعدادات أساسية للمنصة' : 'Basic platform settings'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label>{i18n.language === 'ar' ? 'اسم الموقع' : 'Site Name'}</Label>
                      <Input
                        value={settings.siteName}
                        onChange={(e) => setSettings({...settings, siteName: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'رابط الموقع' : 'Site URL'}</Label>
                      <Input
                        value={settings.siteUrl}
                        onChange={(e) => setSettings({...settings, siteUrl: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'بريد المدير' : 'Admin Email'}</Label>
                      <Input
                        type="email"
                        value={settings.adminEmail}
                        onChange={(e) => setSettings({...settings, adminEmail: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'بريد الدعم' : 'Support Email'}</Label>
                      <Input
                        type="email"
                        value={settings.supportEmail}
                        onChange={(e) => setSettings({...settings, supportEmail: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{i18n.language === 'ar' ? 'السماح بالتسجيل' : 'Enable Registration'}</p>
                        <p className="text-sm text-text-secondary">
                          {i18n.language === 'ar' ? 'السماح للمستخدمين الجدد بالتسجيل' : 'Allow new users to register'}
                        </p>
                      </div>
                      <Switch
                        checked={settings.enableRegistration}
                        onCheckedChange={(checked) => setSettings({...settings, enableRegistration: checked})}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{i18n.language === 'ar' ? 'تفعيل OTP' : 'Enable OTP'}</p>
                        <p className="text-sm text-text-secondary">
                          {i18n.language === 'ar' ? 'تفعيل المصادقة الثنائية' : 'Enable two-factor authentication'}
                        </p>
                      </div>
                      <Switch
                        checked={settings.enableOTP}
                        onCheckedChange={(checked) => setSettings({...settings, enableOTP: checked})}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="security">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'إعدادات الأمان' : 'Security Settings'}</CardTitle>
                  <CardDescription>
                    {i18n.language === 'ar' ? 'إدارة إعدادات الأمان والحماية' : 'Manage security and protection settings'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label>{i18n.language === 'ar' ? 'مدة الجلسة (دقائق)' : 'Session Timeout (minutes)'}</Label>
                      <Input
                        type="number"
                        value={settings.sessionTimeout}
                        onChange={(e) => setSettings({...settings, sessionTimeout: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'محاولات تسجيل الدخول' : 'Max Login Attempts'}</Label>
                      <Input
                        type="number"
                        value={settings.maxLoginAttempts}
                        onChange={(e) => setSettings({...settings, maxLoginAttempts: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{i18n.language === 'ar' ? 'التحقق من البريد الإلكتروني' : 'Email Verification'}</p>
                        <p className="text-sm text-text-secondary">
                          {i18n.language === 'ar' ? 'يتطلب التحقق من البريد عند التسجيل' : 'Require email verification on signup'}
                        </p>
                      </div>
                      <Switch
                        checked={settings.requireEmailVerification}
                        onCheckedChange={(checked) => setSettings({...settings, requireEmailVerification: checked})}
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h3 className="font-semibold mb-4">{i18n.language === 'ar' ? 'إجراءات الأمان' : 'Security Actions'}</h3>
                    <div className="flex gap-3">
                      <Button variant="outline">
                        <RefreshCw className="w-4 h-4 ml-2" />
                        {i18n.language === 'ar' ? 'إعادة تعيين الجلسات' : 'Reset Sessions'}
                      </Button>
                      <Button variant="outline">
                        <Lock className="w-4 h-4 ml-2" />
                        {i18n.language === 'ar' ? 'فرض تغيير كلمات المرور' : 'Force Password Change'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'إعدادات الإشعارات' : 'Notification Settings'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{i18n.language === 'ar' ? 'تفعيل الإشعارات' : 'Enable Notifications'}</p>
                      <p className="text-sm text-text-secondary">
                        {i18n.language === 'ar' ? 'السماح بإرسال الإشعارات للمستخدمين' : 'Allow sending notifications to users'}
                      </p>
                    </div>
                    <Switch
                      checked={settings.enableNotifications}
                      onCheckedChange={(checked) => setSettings({...settings, enableNotifications: checked})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{i18n.language === 'ar' ? 'إشعارات البريد' : 'Email Notifications'}</p>
                      <p className="text-sm text-text-secondary">
                        {i18n.language === 'ar' ? 'إرسال إشعارات عبر البريد الإلكتروني' : 'Send notifications via email'}
                      </p>
                    </div>
                    <Switch
                      checked={settings.enableEmailNotif}
                      onCheckedChange={(checked) => setSettings({...settings, enableEmailNotif: checked})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{i18n.language === 'ar' ? 'إشعارات SMS' : 'SMS Notifications'}</p>
                      <p className="text-sm text-text-secondary">
                        {i18n.language === 'ar' ? 'إرسال إشعارات عبر الرسائل القصيرة' : 'Send notifications via SMS'}
                      </p>
                    </div>
                    <Switch
                      checked={settings.enableSMSNotif}
                      onCheckedChange={(checked) => setSettings({...settings, enableSMSNotif: checked})}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="backup">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'إعدادات النسخ الاحتياطي' : 'Backup Settings'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{i18n.language === 'ar' ? 'تفعيل النسخ الاحتياطي' : 'Enable Backup'}</p>
                      <p className="text-sm text-text-secondary">
                        {i18n.language === 'ar' ? 'نسخ احتياطي تلقائي للبيانات' : 'Automatic data backup'}
                      </p>
                    </div>
                    <Switch
                      checked={settings.backupEnabled}
                      onCheckedChange={(checked) => setSettings({...settings, backupEnabled: checked})}
                    />
                  </div>

                  <div>
                    <Label>{i18n.language === 'ar' ? 'تكرار النسخ' : 'Backup Frequency'}</Label>
                    <select
                      className="w-full mt-2 p-2 border rounded"
                      value={settings.backupFrequency}
                      onChange={(e) => setSettings({...settings, backupFrequency: e.target.value})}
                    >
                      <option value="hourly">{i18n.language === 'ar' ? 'كل ساعة' : 'Hourly'}</option>
                      <option value="daily">{i18n.language === 'ar' ? 'يومياً' : 'Daily'}</option>
                      <option value="weekly">{i18n.language === 'ar' ? 'أسبوعياً' : 'Weekly'}</option>
                      <option value="monthly">{i18n.language === 'ar' ? 'شهرياً' : 'Monthly'}</option>
                    </select>
                  </div>

                  <div className="pt-4 border-t">
                    <Button className="btn-primary">
                      <Database className="w-4 h-4 ml-2" />
                      {i18n.language === 'ar' ? 'إنشاء نسخة احتياطية الآن' : 'Create Backup Now'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="email">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'إعدادات البريد الإلكتروني' : 'Email Settings'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>{i18n.language === 'ar' ? 'خادم SMTP' : 'SMTP Server'}</Label>
                    <Input placeholder="smtp.gmail.com" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{i18n.language === 'ar' ? 'المنفذ' : 'Port'}</Label>
                      <Input placeholder="587" />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'التشفير' : 'Encryption'}</Label>
                      <Input placeholder="TLS" />
                    </div>
                  </div>
                  <div>
                    <Label>{i18n.language === 'ar' ? 'اسم المستخدم' : 'Username'}</Label>
                    <Input type="email" />
                  </div>
                  <div>
                    <Label>{i18n.language === 'ar' ? 'كلمة المرور' : 'Password'}</Label>
                    <Input type="password" />
                  </div>
                  <div className="pt-4">
                    <Button variant="outline">
                      <Mail className="w-4 h-4 ml-2" />
                      {i18n.language === 'ar' ? 'اختبار الاتصال' : 'Test Connection'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SystemSettings;